/**
 * TealerLab — PDP reviews: filter the Judge.me list by rating.
 *
 * The custom histogram in the aside (`.tlr-revs__bars`) drives the native
 * Judge.me review list. Judge.me exposes the filter its own histogram uses:
 *
 *   jdgm.filterReviewsByRating(rating | null, jdgm.$(widget))
 *
 * `widget` is the `.jdgm-review-widget` container. The call is only valid once
 * Judge.me has set its callbacks on that element (`before-callback`), which it
 * does when the widget enters the viewport — hence the readiness poll rather
 * than a call on load. Until then the rows stay disabled: no dead click.
 *
 * Counts: the shop has `widget_show_histogram: false`, so Judge.me DELETES the
 * pre-rendered histogram during setup. Its rows carry the real per-rating
 * counts, so they are read once here, before Judge.me runs — the Liquid
 * fallback percentages of the section are store-entered and disagree with
 * reality (a row saying 0 that filters to 4 reviews is a bug).
 *
 * Degradation: no Judge.me, no histogram data or a failing fetch all end on a
 * readable static histogram plus a message in the live region — never a
 * spinner that never stops.
 */

const SECTION = '[data-tlr-revs-root]';
const WIDGET = '#judgeme_product_reviews';
const BODY = '.jdgm-rev-widg__body';
const SPINNER = '.jdgm-rev-widg__paginate-spinner-wrapper';

/**
 * Judge.me sets up a widget when it is scrolled near, and that setup is a
 * chain of network round-trips: poll only from there, and long enough that a
 * slow connection still ends with working rows rather than dead ones.
 */
const READY_TIMEOUT = 180000;
const READY_INTERVAL = 500;
/** A fetch that never comes back must not leave the list hidden forever. */
const FETCH_TIMEOUT = 12000;
const FETCH_INTERVAL = 250;

// `| t` HTML-escapes its output, so a translated apostrophe reaches us as
// `&#39;`. These strings go into textContent, where entities are not decoded.
const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

function translations() {
  const holder = document.querySelector('script[data-tlr-revs-i18n]');
  try {
    const raw = JSON.parse(holder?.dataset.tlrRevsI18n || '{}');
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value === 'string') raw[key] = value.replace(/&(amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m]);
    }
    return raw;
  } catch (_) {
    return {};
  }
}

/** Per-rating review counts read from the server-rendered Judge.me histogram. */
function histogramCounts(widget) {
  const counts = new Map();
  widget.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((row) => {
    const rating = Number(row.dataset.rating);
    const frequency = Number(row.dataset.frequency);
    if (Number.isInteger(rating) && Number.isFinite(frequency)) counts.set(rating, frequency);
  });
  return counts.size ? counts : null;
}

function paintCounts(rows, counts) {
  let total = 0;
  counts.forEach((value) => {
    total += value;
  });
  rows.forEach((row) => {
    const value = counts.get(Number(row.dataset.tlrBarRow));
    if (value === undefined) return;
    const count = row.querySelector('[data-tlr-bar-count]');
    const fill = row.querySelector('[data-tlr-bar-fill]');
    if (count) count.textContent = String(value);
    if (fill) fill.style.width = (total > 0 ? Math.round((value / total) * 100) : 0) + '%';
  });
}

function canFilter(widget) {
  const jdgm = window.jdgm;
  return Boolean(
    jdgm &&
      typeof jdgm.filterReviewsByRating === 'function' &&
      typeof jdgm.$ === 'function' &&
      typeof jdgm.$(widget).data('before-callback') === 'function'
  );
}

function init() {
  const root = document.querySelector(SECTION);
  if (!root) return;
  const bars = root.querySelector('[data-tlr-revs-bars]');
  const widget = root.querySelector(WIDGET);
  if (!bars || !widget) return;

  const strings = translations();
  const status = root.querySelector('[data-tlr-filter-status]');
  const rows = Array.from(bars.querySelectorAll('[data-tlr-bar-row]'));
  if (!rows.length) return;

  // Read before Judge.me deletes the histogram; null keeps the Liquid values.
  const counts = histogramCounts(widget);
  if (counts) paintCounts(rows, counts);

  let active = null;
  let pending = 0;

  const announce = (text) => {
    if (status) status.textContent = text || '';
  };

  const paintPressed = () => {
    rows.forEach((row) => {
      row.setAttribute('aria-pressed', String(Number(row.dataset.tlrBarRow) === active));
    });
  };

  /** A row can only filter to a non-empty result. */
  const eligible = (row) => {
    if (!counts) return true;
    return (counts.get(Number(row.dataset.tlrBarRow)) || 0) > 0;
  };

  const enableRows = () => {
    rows.forEach((row) => {
      row.disabled = !eligible(row);
    });
  };

  /** Judge.me hides the list while fetching; put it back if nothing returns. */
  const restoreList = () => {
    const body = widget.querySelector(BODY);
    const spinner = widget.querySelector(SPINNER);
    if (body) body.style.display = '';
    if (spinner) spinner.style.display = 'none';
  };

  const watchFetch = (token, previous) => {
    const started = Date.now();
    const tick = () => {
      if (token !== pending) return;
      const body = widget.querySelector(BODY);
      if (body && body.offsetParent !== null) return;
      if (Date.now() - started > FETCH_TIMEOUT) {
        active = previous;
        paintPressed();
        restoreList();
        announce(strings.filterError || '');
        return;
      }
      setTimeout(tick, FETCH_INTERVAL);
    };
    setTimeout(tick, FETCH_INTERVAL);
  };

  /**
   * On a stacked layout the list sits far below the bars: bring it back.
   * Measured on the widget container, not on the list: Judge.me hides the list
   * while fetching and a hidden element has an all-zero bounding rect.
   */
  const revealList = () => {
    const rect = widget.getBoundingClientRect();
    if (rect.top <= window.innerHeight * 0.9) return;
    const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 80;
    window.scrollTo({ top: rect.top + window.scrollY - offset - 16, behavior: 'smooth' });
  };

  const applyFilter = (rating, row) => {
    if (!canFilter(widget)) {
      announce(strings.filterError || '');
      return;
    }
    const previous = active;
    active = rating;
    paintPressed();
    announce(rating === null ? strings.filterAll || '' : row?.dataset.tlrBarActiveLabel || '');
    const token = ++pending;
    try {
      window.jdgm.filterReviewsByRating(rating, window.jdgm.$(widget));
    } catch (_) {
      active = previous;
      paintPressed();
      restoreList();
      announce(strings.filterError || '');
      return;
    }
    watchFetch(token, previous);
    revealList();
  };

  bars.addEventListener('click', (event) => {
    const row = event.target.closest('[data-tlr-bar-row]');
    if (!row || row.disabled) return;
    const rating = Number(row.dataset.tlrBarRow);
    applyFilter(active === rating ? null : rating, row);
  });

  const waitForJudgeme = () => {
    const started = Date.now();
    const tick = () => {
      if (canFilter(widget)) {
        enableRows();
        return;
      }
      if (Date.now() - started > READY_TIMEOUT) return;
      setTimeout(tick, READY_INTERVAL);
    };
    tick();
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        waitForJudgeme();
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(bars);
  } else {
    waitForJudgeme();
  }
}

init();
